// Geboortevenster · Opmaak: tijden op de geboorteklok (IANA-zone van het document) en korte duren/deltas.
// Overzicht van de feature: EgituraPlatform/docs/human-design/hd-birth-window.md

/**
 * Tijd- en duurweergave voor het geboortevenster: tijden op de geboorteklok
 * (IANA-zone van het document) en korte duren zoals "4 u 3 m" / "4 h 3 m".
 */

/**
 * Teksten (onder `humanDesign.chart.birthWindow`) die over de geboorte gaan. Een
 * chart van een moment (Just Now, Rave New Year; Steven 2026-10-25) heeft geen
 * geboorte maar een tijdstip: daar komt de variant onder `moment.<key>`.
 */
const MOMENT_TEXT_KEYS: ReadonlySet<string> = new Set([
  'title',
  'subtitle',
  'bornAt',
  'birthClock',
  'loading',
  'hint',
  'hintPhone',
  'ariaLanes',
  'legend.birth',
  'indicator.title',
  'indicator.open',
  'popup.setTimeHere',
  'chart.atBirth',
  'commit.delta',
  'commit.apply',
  'commit.reset',
  'commit.confirmTitle',
  'commit.applied',
]);

/** Sleutel (relatief t.o.v. `humanDesign.chart.birthWindow`) voor een chart van een geboorte of van een moment. */
export function hdBirthWindowTextKey(key: string, moment: boolean): string {
  return moment && MOMENT_TEXT_KEYS.has(key) ? `moment.${key}` : key;
}

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

function unitLabels(lang: string): { d: string; h: string; m: string; s: string } {
  return lang.toLowerCase().startsWith('nl') ? { d: 'd', h: 'u', m: 'm', s: 's' } : { d: 'd', h: 'h', m: 'm', s: 's' };
}

/** Korte duur zonder teken: "43 s", "7 m", "4 u 3 m", "1 d 2 u". Nooit meer dan twee eenheden. */
export function formatBirthWindowDuration(ms: number, lang: string): string {
  const u = unitLabels(lang);
  const abs = Math.abs(ms);
  if (abs < MINUTE_MS) return `${Math.round(abs / 1000)} ${u.s}`;
  if (abs < HOUR_MS) {
    const minutes = Math.floor(abs / MINUTE_MS);
    const seconds = Math.round((abs - minutes * MINUTE_MS) / 1000);
    return seconds > 0 && minutes < 10 ? `${minutes} ${u.m} ${seconds} ${u.s}` : `${minutes} ${u.m}`;
  }
  if (abs < DAY_MS) {
    const hours = Math.floor(abs / HOUR_MS);
    const minutes = Math.round((abs - hours * HOUR_MS) / MINUTE_MS);
    return minutes > 0 ? `${hours} ${u.h} ${minutes} ${u.m}` : `${hours} ${u.h}`;
  }
  const days = Math.floor(abs / DAY_MS);
  const hours = Math.round((abs - days * DAY_MS) / HOUR_MS);
  return hours > 0 ? `${days} ${u.d} ${hours} ${u.h}` : `${days} ${u.d}`;
}

/** Duur met teken t.o.v. de geboorte: "+7 m", "−4 u 3 m". */
export function formatBirthWindowSignedDelta(ms: number, lang: string): string {
  const sign = ms < 0 ? '−' : '+';
  return `${sign}${formatBirthWindowDuration(ms, lang)}`;
}

export interface BirthClockOptions {
  readonly seconds?: boolean;
  readonly date?: boolean;
}

/** Tijd op de geboorteklok; onbekende of ongeldige zone valt terug op UTC. */
export function formatBirthClock(ms: number, timeZone: string | undefined, lang: string, options: BirthClockOptions = {}): string {
  const date = new Date(ms);
  const zone = resolveTimeZone(timeZone);
  const time = new Intl.DateTimeFormat(lang, {
    hour: '2-digit',
    minute: '2-digit',
    second: options.seconds ? '2-digit' : undefined,
    hourCycle: 'h23',
    timeZone: zone,
  }).format(date);
  if (!options.date) return time;
  const day = new Intl.DateTimeFormat(lang, { day: 'numeric', month: 'short', timeZone: zone }).format(date);
  return `${day} ${time}`;
}

function resolveTimeZone(timeZone: string | undefined): string {
  if (!timeZone) return 'UTC';
  try {
    new Intl.DateTimeFormat('en', { timeZone }).format(0);
    return timeZone;
  } catch {
    return 'UTC';
  }
}

/** Zelfde kalenderdag op de geboorteklok? (bepaalt of een tijd een datum nodig heeft). */
export function sameBirthClockDay(aMs: number, bMs: number, timeZone: string | undefined): boolean {
  const zone = resolveTimeZone(timeZone);
  const key = (ms: number) => new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit', timeZone: zone }).format(new Date(ms));
  return key(aMs) === key(bMs);
}
